/**
 * Filing Types
 *
 * TypeScript types for the filing history API.
 * Mirrors backend DTOs from app/modules/filing/dtos.py
 *
 * Session 3.2: Filing History Frontend
 */

// =============================================================================
// ENUMS & CONSTANTS
// =============================================================================

/**
 * Filing status values.
 * GENERATED: C10 file has been created but not yet submitted to NIB
 * FILED: Filing has been submitted to NIB
 * AMENDED: Filing has been amended after submission
 */
export type FilingStatus = 'GENERATED' | 'FILED' | 'AMENDED';

/**
 * C10 format type.
 * HOSPITALITY: 115-byte format with gratuity fields
 * NON_HOSPITALITY: 79-byte standard format
 */
export type FilingFormatType = 'HOSPITALITY' | 'NON_HOSPITALITY';

// =============================================================================
// CORE TYPES
// =============================================================================

/**
 * Employee data within a filing.
 * Represents a snapshot of employee data at the time of filing.
 */
export interface FilingEmployee {
  /** Employee record ID */
  id: number;
  /** National Insurance number (8 digits) */
  employee_ni: string;
  /** Employee first name */
  first_name: string;
  /** Employee last name */
  last_name: string;
  /** NIB category (1-7) */
  category: number;
  /** Basic wages as decimal string (e.g., "1234.56") */
  basic_wages: string;
  /** Gratuity amount as decimal string (null for non-hospitality) */
  gratuity: string | null;
  /** Number of weeks worked in period */
  weeks_worked: number | null;
  /** Total contribution as decimal string */
  total_contribution: string;
}

/**
 * Summary of a filing for list view.
 * Contains key information for displaying in a filing history list.
 * Does not include individual employee data.
 */
export interface FilingSummary {
  /** UUID of the filing */
  id: string;
  /** Contribution month (1-12) */
  contribution_month: number;
  /** Contribution year (e.g., 2025) */
  contribution_year: number;
  /** Human-readable period display (e.g., "January 2025") */
  period_display: string;
  /** C10 format type */
  format_type: FilingFormatType;
  /** Number of employees in the filing */
  employee_count: number;
  /** Total wages as decimal string */
  total_wages: string;
  /** Total contributions as decimal string */
  total_contributions: string;
  /** Filing status */
  status: FilingStatus;
  /** When the C10 was generated */
  generated_at: string;
  /** When it was marked as filed (null if not yet filed) */
  filed_at: string | null;
}

/**
 * Full filing detail with employees.
 * Contains all filing information including individual employee records.
 * Used for the filing detail view.
 */
export interface FilingDetail extends FilingSummary {
  /** Optional notes about the filing */
  notes: string | null;
  /** List of employees in the filing */
  employees: FilingEmployee[];
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/**
 * Pagination metadata for filing list.
 */
export interface FilingPagination {
  /** Total number of filings */
  total: number;
  /** Current page number (1-indexed) */
  page: number;
  /** Items per page */
  per_page: number;
  /** Total number of pages */
  total_pages: number;
}

/**
 * Paginated filing list response.
 * Contains a page of filings with pagination metadata.
 */
export interface FilingListResponse {
  /** List of filing summaries */
  filings: FilingSummary[];
  /** Pagination metadata */
  pagination: FilingPagination;
}

/**
 * Filing statistics for a user.
 */
export interface FilingStats {
  /** Total number of filings */
  total_filings: number;
  /** Number of filings marked as filed */
  filed_count: number;
  /** Number of pending filings */
  pending_count: number;
  /** Total employees across all filings */
  total_employees: number;
}

// =============================================================================
// REQUEST TYPES
// =============================================================================

/**
 * Parameters for listing filings.
 */
export interface FilingListParams {
  /** Filter by year */
  year?: number;
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  per_page?: number;
}

/**
 * Request to update filing notes.
 */
export interface UpdateNotesRequest {
  /** New notes content */
  notes: string;
}

// =============================================================================
// UI HELPER TYPES
// =============================================================================

/**
 * Status badge configuration.
 */
export interface FilingStatusConfig {
  /** Display label */
  label: string;
  /** Tailwind background color class */
  bgColor: string;
  /** Tailwind text color class */
  textColor: string;
  /** Tailwind border color class */
  borderColor: string;
}

/**
 * Status configuration map.
 */
export const FILING_STATUS_CONFIG: Record<FilingStatus, FilingStatusConfig> = {
  GENERATED: {
    label: 'Generated',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
  FILED: {
    label: 'Filed',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  AMENDED: {
    label: 'Amended',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
};

/**
 * Format type labels for display.
 */
export const FORMAT_TYPE_LABELS: Record<FilingFormatType, string> = {
  HOSPITALITY: 'Hospitality',
  NON_HOSPITALITY: 'Non-Hospitality',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get status configuration for a filing status.
 */
export function getStatusConfig(status: FilingStatus): FilingStatusConfig {
  return FILING_STATUS_CONFIG[status];
}

/**
 * Format a monetary value for display.
 * @param value - Decimal string from API
 * @returns Formatted string with currency symbol
 */
export function formatCurrency(value: string): string {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'BSD',
    minimumFractionDigits: 2,
  }).format(num);
}

/**
 * Format employee full name.
 */
export function formatEmployeeName(employee: FilingEmployee): string {
  return `${employee.first_name} ${employee.last_name}`;
}

/**
 * Get category label for a category number.
 */
export function getCategoryLabel(category: number): string {
  const labels: Record<number, string> = {
    1: 'Regular',
    2: 'Retired',
    3: 'Senior 65-69',
    4: 'Retired 65-69',
    5: 'Senior 70+',
    6: 'Retired 70+',
    7: 'Summer Student',
  };
  return labels[category] || `Category ${category}`;
}
